import { forwardRef, type ReactNode, type MouseEvent } from "react";
import { INTAKE_URLS, LAUNCHED, type IntakeKey } from "@/config/intake";
import { waitlistModal } from "@/stores/waitlist-modal";

type Props = {
  /** Which intake URL to use post-launch. */
  intakeKey?: IntakeKey;
  /** Override the intake URL entirely (e.g. with ?state=XX appended). */
  intakeHref?: string;
  /** Analytics placement label — appears in waitlist_join events. */
  placement: string;
  /** Pre-launch label override. Defaults to children. */
  waitlistLabel?: ReactNode;
  /** Force pre-launch behavior even if LAUNCHED === true (rare). */
  forceWaitlist?: boolean;
  className?: string;
  children: ReactNode;
  /** Side-effect onClick (e.g. close mobile menu). Runs in both branches. */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  "aria-label"?: string;
};

/**
 * Drop-in CTA. Renders a <button> that opens the waitlist modal pre-launch,
 * or the original <a href={INTAKE_URLS.x}> post-launch — preserving className
 * so existing styling stays pixel-identical.
 */
export const MembershipCTA = forwardRef<HTMLElement, Props>(function MembershipCTA(
  { intakeKey = "default", intakeHref, placement, waitlistLabel, forceWaitlist, className, children, onClick, ...rest },
  ref,
) {
  const showWaitlist = !LAUNCHED || forceWaitlist;

  if (showWaitlist) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={(e) => {
          waitlistModal.open(placement);
          onClick?.(e);
        }}
        className={className}
        {...rest}
      >
        {waitlistLabel ?? children}
      </button>
    );
  }

  const href = intakeHref ?? INTAKE_URLS[intakeKey];
  return (
    <a
      ref={ref as React.Ref<HTMLAnchorElement>}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={className}
      {...rest}
    >
      {children}
    </a>
  );
});
